/**
 * Sync-Client – die HTTP-Schnittstelle zum Sync-Server (sync-server/README.md).
 * Überträgt nur bereits verschlüsselte Bytes.
 */

/** Ein anderes Gerät hat den Zustand zwischendurch geändert */
export class SyncConflictError extends Error {
  constructor() {
    super('Der Stand auf dem Server hat sich zwischendurch geändert.');
    this.name = 'SyncConflictError';
  }
}

/**
 * @param {{ serverUrl: string, token: string }} connection
 */
export function createClient({ serverUrl, token }) {
  async function request(path, init = {}) {
    let response;
    try {
      response = await fetch(serverUrl + path, {
        ...init,
        headers: { Authorization: `Bearer ${token}`, ...init.headers },
      });
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      throw new Error('Der Sync-Server ist nicht erreichbar.', { cause: error });
    }
    if (response.status === 401) {
      throw new Error('Der Sync-Server lehnt den Zugangsschlüssel ab.');
    }
    return response;
  }

  function ensureOk(response) {
    if (response.status === 413) {
      throw new Error('Zu viele Daten für den Sync-Server – seine Größengrenze ist erreicht.');
    }
    if (!response.ok) {
      throw new Error(`Der Sync-Server meldet einen Fehler (HTTP ${response.status}).`);
    }
  }

  const bytes = async (response) => new Uint8Array(await response.arrayBuffer());

  return {
    async ping() {
      ensureOk(await request('/v1/ping'));
    },

    /** @returns {Promise<{ data: Uint8Array, etag: string }|null>} */
    async getState(spaceId) {
      const response = await request(`/v1/spaces/${spaceId}/state`);
      if (response.status === 404) return null;
      ensureOk(response);
      return { data: await bytes(response), etag: response.headers.get('ETag') };
    },

    /**
     * Schreibt den Zustand, wenn er noch auf etag steht (null = noch keiner da)
     * @returns {Promise<string>} neuer ETag
     */
    async putState(spaceId, data, etag) {
      const precondition = etag ? { 'If-Match': etag } : { 'If-None-Match': '*' };
      const response = await request(`/v1/spaces/${spaceId}/state`, {
        method: 'PUT',
        headers: precondition,
        body: data,
      });
      if (response.status === 412) throw new SyncConflictError();
      ensureOk(response);
      return response.headers.get('ETag');
    },

    /** @returns {Promise<Uint8Array|null>} */
    async getAudio(spaceId, id, signal) {
      const response = await request(`/v1/spaces/${spaceId}/audio/${id}`, { signal });
      if (response.status === 404) return null;
      ensureOk(response);
      return bytes(response);
    },

    async putAudio(spaceId, id, data) {
      ensureOk(await request(`/v1/spaces/${spaceId}/audio/${id}`, { method: 'PUT', body: data }));
    },

    /** @returns {Promise<Set<string>>} die vorhandenen Kennungen */
    async existingAudio(spaceId, ids) {
      const response = await request(`/v1/spaces/${spaceId}/audio/exists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      ensureOk(response);
      return new Set((await response.json()).ids);
    },
  };
}
